// How a planning cell is coloured, in one place.
//
// Two views draw the same entries now - the full grid on the Planning page and
// the month calendar on the home page - and they have to agree: a trip that is
// green in one and green in the other is the same trip, which is the entire job
// these colours do. Written once here rather than copied, because the copy is
// how they stop agreeing, and this repository already tracks three functions
// that drifted that way.
//
// On the fills themselves: they group, they do not mean. The same green sits on
// one client's trips month after month because whoever filled the sheet reached
// for the colour already there, and the same red is on both a trip to the USA
// and on NO TRASFERTE. A reader that concluded "red means unavailable" would be
// wrong half the time.

// How far each fill is pulled toward white. The workbook uses Excel's palette
// at full strength - #FFFF00, #FF0000 - which on a screen is glare rather than
// colour. 0.62 came out washed; this keeps the body of each hue.
const PASTEL = 0.42;

// Only reached by a cell the sheet left unfilled. Almost every NO TRASFERTE is
// already red in the workbook and almost every FERIE already yellow.
export const KIND_FALLBACK = { unavailable: "#FF0000", leave: "#FFFF00" };
export const PLAIN_FILL = "#94A3B8";

function parseHex(hex) {
  const n = parseInt(String(hex).replace("#", "").slice(0, 6), 16);
  if (Number.isNaN(n)) return [148, 163, 184];
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

/** The fill pulled toward white by PASTEL: the same hue, none of the glare. */
export function pastel(hex) {
  const [r, g, b] = parseHex(hex);
  const mix = (c) => Math.round(c + (255 - c) * PASTEL);
  return `rgb(${mix(r)}, ${mix(g)}, ${mix(b)})`;
}

/**
 * Text for a pastel block: the same hue pulled toward black, so the block is
 * still one colour - olive on pale yellow, wine on pale red - rather than a
 * coloured box with a grey label in it.
 */
export function ink(hex) {
  const [r, g, b] = parseHex(hex);
  const mix = (c) => Math.round(c * 0.38);
  return `rgb(${mix(r)}, ${mix(g)}, ${mix(b)})`;
}

/**
 * The colour an entry is drawn in, whatever the sheet did or did not say.
 *
 * The fill when there is one, then the convention for what the words mean, then
 * a neutral. Both views go through this, so an entry the sheet left unfilled
 * looks the same on the calendar as it does on the grid.
 */
export function entryHue(entry) {
  if (!entry) return PLAIN_FILL;
  return entry.color || KIND_FALLBACK[entry.kind] || PLAIN_FILL;
}
